import React from 'react';
import { TemplateType } from '../TemplateType';
import usePdfContent, { PdfContentParams } from '../usePdfContent';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { makePath } from 'apps/Pdf/helpers/helpers';

import Grid from '@material-ui/core/Grid';

import PdfContent from 'apps/Pdf/components/common/PdfContent';
import Title from 'apps/Pdf/components/common/Title';
import Glossary from 'apps/Pdf/components/Glossary/Glossary';
import H1 from 'apps/Pdf/components/common/H1';
import P from 'apps/Pdf/components/common/P';
//import H2 from 'apps/Pdf/components/common/H2';
import Loading from 'apps/Pdf/components/common/Loading';

import useGeneralStyles from 'apps/Pdf/styles/general';
import useColorStyles from 'apps/Pdf/styles/colors';
import Signature from 'apps/Pdf/components/Signature/Signature';
import { PriceListContent } from './PriceListInterface';
import FeeLine from './FeeLine';
import DetailFeeLine from './DetailFeeLine';
import { fractionToPercent } from 'helpers/helpers';
import { Currency } from 'enums';
import { PdfCategory } from '../Interfaces';

const PriceList = (params: PdfContentParams) => {
  const { t } = useTranslation();
  const styles = useGeneralStyles();

  let templateType = TPL_PRICE_LIST;
  const location = useLocation();
  if (location.pathname.slice(0, -1) === makePath(TPL_NEW_PRICE_LIST)) {
    templateType = TPL_NEW_PRICE_LIST;
  }

  const pdfContent = usePdfContent<PriceListContent>({ templateType, category: PdfCategory.SIGNATURE, ...params });
  const data = pdfContent?.data ? pdfContent.data : ({} as PriceListContent);
  const currency = pdfContent?.data ? pdfContent.data.currency : Currency.CZK;
  const colors = useColorStyles({ code: pdfContent?.data?.header.signedVia || '' });

  return pdfContent ? (
    <PdfContent errors={pdfContent?.errors} className={colors.root} onload afterLoad={params.afterLoad}>
      <Title>
        Sazebník služeb
        {data.header.title && <span className="sub">(platný od 1. 1. 2025)</span>}
      </Title>
        <>
          <H1>Vstupní poplatky</H1>

          {templateType === TPL_PRICE_LIST && !params.change && (
            <>
              <FeeLine
                title={t('fees_entryFee')}
                amount={data.singleFees.individualFee ? data.singleFees.individualFee : data.singleFees.calculatedFee}
                currency={currency}
                variant="main"
              />

              <FeeLine
                title="Předplacená částka: Jednorázové investice"
                amount={data.singleFees.initialDeposit}
                currency={currency}
              />
              <FeeLine
                title="Předplacená částka: Budoucí pravidelné investice"
                amount={data.singleFees.periodicDeposits}
                currency={currency}
              />
              {/*data.singleFees.employer && (
            <>
              {data.singleFees.clientInvestments && (
                <FeeLine
                  title={t('fees_investments_client')}
                  amount={data.singleFees.clientInvestments}
                  currency={currency}
                />
              )}
              {data.singleFees.employerInvestments && (
                <FeeLine
                  title={t('fees_investments_employer')}
                  amount={data.singleFees.employerInvestments}
                  currency={currency}
                />
              )}
            </>
          )*/}

              <FeeLine
                title={t('fees_investments_sum')}
                amount={data.singleFees.totalInvestments}
                currency={currency}
              />
              <FeeLine title={t('fees_feeRate')} percentage={data.singleFees.feeRate} />
              <FeeLine title={t('fees_entryFee')} amount={data.singleFees.calculatedFee} currency={currency} />

              {data.singleFees.individualFee && (
                <>
                  <FeeLine title={t('fees_discount')} percentage={data.singleFees.discount} currency={currency} />
                  <FeeLine
                    title={t('fees_calculated_resultFee')}
                    amount={data.singleFees.individualFee}
                    currency={currency}
                  />
                </>
              )}
            </>
          )}

          <FeeLine
            title={t('fees_contFee')}
            percentage={data.singleFees.continuousIndividual ? data.singleFees.individualRate : data.singleFees.feeRate}
            variant="main"
          />
          <FeeLine title={t('fees_contFee_detail')} variant="detail" />
          <FeeLine title={t('fees_feeRate')} percentage={data.singleFees.feeRate} />

          {data.singleFees.continuousIndividual && (
            <>
              <FeeLine title={t('fees_discount')} percentage={data.singleFees.rateDiscount} />
              <FeeLine title={t('fees_individual_rate')} percentage={data.singleFees.individualRate} />
            </>
          )}
          {data.singleFees.cappingApplied && (
            <FeeLine title={t('fees_cappedFee')} amount={data.singleFees.cappedFee} currency={currency} />
          )}

          <FeeLine
            title={t('fees_closing_fee')}
            //of percentage={data.singleFees.closingFee}
            amount={data.singleFees.closingFee}
            currency={currency}
            variant="main"
          />

          <P>Všechny poplatky jsou uvedeny včetně DPH, není-li služba od DPH osvobozena</P>
        </>


      <div className={styles.avoidPageBreak}>
        <H1>
          Poplatek za využívání služby {data.taxMode === 'PPP' ? 'přijímání a předávání pokynů' : data.taxMode === 'OBH' ? 'obhospodařování' : ''}
        </H1>

        <Grid container alignItems="center" className={`${styles.lines}`}>
          <Grid item xs={7} />
          <Grid item xs={2} className={`${styles.right} ${styles.supplement}`}>
            Celkem{!!data.taxMode && <><br />nově{' '}<span style={{ textDecoration: 'line-through' }}>původně</span></>}
          </Grid>
          <Grid item xs={1} className={`${styles.center} ${styles.halfXs}`}>=</Grid>
          <Grid item xs={1} className={`${styles.center} ${styles.supplement}`}>
            Odměna OCP
            {/*<br />
            (vč. DPH)*/}
          </Grid>
          <Grid item xs={1} className={`${styles.center} ${styles.halfXs}`}>+</Grid>
          <Grid item xs={1} className={`${styles.center} ${styles.supplement}`}>
            Odměna IZ
          </Grid>
        </Grid>

        {data.regularFees.performance.total !== 0 && (
          <DetailFeeLine
            title={'Výkonnostní (z celkového zisku)'}
            percentage={data.regularFees.performance.total}
            share={[data.regularFees.performance.ocp, data.regularFees.performance.iz]}
          />
        )}

        <Grid container alignItems="center" style={{ padding: '4px 0 0 4px' }}>
          <Grid item xs={4} className={`${styles.regularText}`}>
            Standardní (p.a.)
          </Grid>
          <Grid xs={8} />
        </Grid>

        {data.regularFees.service.map((fees, index) => (
          <DetailFeeLine
            //heading={index === 0 ? 'Počítaný z průměrných aktiv pod správou (ročně)' : ''}
            subheading={index === 0 ? t('fees_service_portfolio') : ''}
            title={t(`fees_${fees.title}_${currency}`)}
            original={data.taxMode && data.originalRegularFees ? data.originalRegularFees.service[index].total : undefined}
            percentage={fees.total}
            //currency={currency}
            key={`fee_${index}`}
            //pa
            share={[fees.ocp, fees.iz]}
          />
        ))}
        <DetailFeeLine
          title={''}
          heading={'Snížený (p.a.)'}
          subheading={t('fees_service_lower_portfolio')}
          original={data.taxMode && data.originalRegularFees ? data.originalRegularFees.serviceLow.total : undefined}
          percentage={data.regularFees.serviceLow.total}
          share={[data.regularFees.serviceLow.ocp, data.regularFees.serviceLow.iz]}
          //currency={currency}
          //max
          //pa
        />
        <P>Všechny poplatky jsou uvedeny včetně DPH, není-li služba od DPH osvobozena</P>
        <P>* Pokud je v portfoliu instrument úročená hotovost, nebude účtovaná odměna IZ</P>
      </div>

      <div className={styles.avoidPageBreak}>
        <H1>{t('fees_additional_pdf')}</H1>
        <FeeLine
          title={t('fees_rebalancing')}
          amount={data.regularFees.common.rebalancingFee}
          currency={currency}
          zeroFee
        />
        <FeeLine
          title={t('fees_reallocation')}
          amount={data.regularFees.common.reallocationFee}
          currency={currency}
          zeroFee
        />
        {/*<FeeLine title={t('fees_custody')} amount={data.regularFees.common.custodyFee} currency={currency} zeroFee />*/}
        <FeeLine
          title={t('fees_accountManagement')}
          amount={data.regularFees.common.accountManagementFee}
          currency={currency}
          zeroFee
        />

        <FeeLine
          title={t('fees_reporting')}
          amount={data.regularFees.common.reportingFee}
          currency={currency}
          zeroFee
        />

        <FeeLine
          title={
            'Při nákupu a prodeji cizích měn se uplatňují mezibankovní měnové kurzy navýšené o následující marži obchodníka:'
          }
        />

        <FeeLine
          title={t('fees_spotHedgingFee')}
          percentage={data.regularFees.common.spotHedgingFee}
          max
          variant="sub"
        />

        {currency === Currency.CZK && (
          <FeeLine title={t('fees_forexFee')} percentage={data.regularFees.common.forexFee} max variant="sub" />
        )}

        {/* //nf
        <FeeLine
          title={t('fees_transactional_fee')}
          subtitle={t('fees_transactional_fee_detail')}
        />

        <FeeLine
          title={'Převod cenných papírů od jiné finanční instituce'}
          percentage={data.singleFees.transferFromFee}
          zeroFee
        />
        */}

        <FeeLine
          title={'Převod cenných papírů k jiné finanční instituci'}
          currency={currency}
          customValue={
            <>
              {data.singleFees.transferToFee === 0
                ? 'v ceně poplatku'
                : fractionToPercent(data.singleFees.transferToFee, 1)}{' '}
              z převáděné hodnoty
              <br />
              <span className={styles.supplement}>{t(`fees_transferFeeMinMax_${currency}`)}</span>
            </>
          }
        />
      </div>
      <div className={styles.avoidPageBreak}>
        <H1>Poplatky účtované správci ETF fondů (TER)</H1>
        <FeeLine
          title="Průměrná nákladovost nabízených ETF (dle vybraného portfolia)"
          percentage={data.regularFees.common.averageTer}
          wideTitle
          max
        />
      </div>
      <Glossary
        keys={[
          'entryFee',
          'prepaidValue',
          'contFees',
          data.singleFees.cappingApplied ? 'cappedFee' : '',
          'serviceFee',
          //'avgPortfolioValue',
          data.regularFees.performance.total !== 0 ? 'performanceFee' : '',
        ]}
      />
      <Signature protocol={data.signedInfo} />
    </PdfContent>
  ) : params.preview ? (
    <Loading />
  ) : null;
};

const TPL_PRICE_LIST: TemplateType = 'priceList';
const TPL_NEW_PRICE_LIST: TemplateType = 'newPriceList';

export { TPL_PRICE_LIST, TPL_NEW_PRICE_LIST, PriceList };
